import {
  getAllCompositeSegments,
  getAllSegments,
  getCompositeSegmentById
} from './clipUpdateDataUtilsWithSplits/compositeUtils';
import { getSegmentsTrack } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/utils';
import { Segment } from '@/App/remotion/MainPlayer/types/Segment/Segment';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import { TrackType } from '@/App/remotion/MainPlayer/types/Track/Type';
import { ElementsTrack } from '@/App/remotion/MainPlayer/types/Track/ElementsTrack';
import { CompositionConfig } from '@/App/remotion/MainPlayer/types/CompositionConfig';
import { Clip } from '@/domains/asset';
import { getClipFPS } from '@/libs/clipUtils';
import { isSplitsInTimelineEnabled } from '@/stores/clip';

export const getCompositeSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    const segmentsTrack = clip.asset_metadata.player_config && getSegmentsTrack(clip.asset_metadata.player_config);
    return segmentsTrack?.segments?.find(segment => segment.type === SegmentType.COMPOSITE);
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    return getCompositeSegmentById(clip, segmentId);
  }
  return getAllCompositeSegments(clip)[0];
};

export const getDeletesForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.deletes || {};
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getAllSegments(clip).find(segment => segment.id === segmentId);
    if (!segment) {
      throw new Error('Segment not found');
    }
    if (segment.type !== SegmentType.COMPOSITE && segment.type !== SegmentType.VIDEO) {
      return {};
    }
    return segment.segment_metadata?.deletes || {};
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.deletes || {};
};

export const getEditsForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled) {
    return clip.asset_metadata.edits;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.edits || {};
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.edits || {};
};

export const getMergesForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.merges;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.merges || [];
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.merges || [];
};

export const getExcludesForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.excludes;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.excludes || [];
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.excludes || [];
};

export const getMaxVideoDurationForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.maxVideoDuration;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.maxVideoDuration || 0;
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.maxVideoDuration || 0;
};

/**
 * Retrieves captions for a specific segment or the entire clip.
 *
 * When splits are enabled and the clip has multiple segments, this function returns captions
 * from the segment's metadata. Otherwise, it returns captions from the clip's asset metadata.
 *
 * @param clip - The clip to retrieve captions from
 * @param segmentId - Optional segment ID. Required when splits are enabled and multiple segments exist
 * @returns An array of caption words (Word[]) for the specified segment, or the clip's captions if splits are disabled
 * @throws {Error} If splits are enabled, multiple segments exist, and no segmentId is provided
 *
 * @remarks
 * - Returns clip-level captions when splits are not enabled or player config is missing
 * - When splits are enabled with a single segment, returns captions from the first segment
 * - When splits are enabled with multiple segments, segmentId is required to identify the specific segment
 * - Returns an empty array if segment metadata captions are not found
 */
export const getCaptionsForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config || !clip.asset_metadata.player_config.playerConfig) {
    return clip.asset_metadata.captions;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.captions || [];
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.captions || [];
};

export const getFitForSegment = (segment: Segment, clip: Clip) => {
  if (segment.type !== SegmentType.COMPOSITE) {
    return undefined;
  }
  return (isSplitsInTimelineEnabled(clip) ? segment.segment_metadata?.fitMode : clip.asset_metadata.fit) || undefined;
};

export const getScenesForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;

  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.scenes;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.scenes || [];
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.scenes || [];
};

export const getStartForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.start;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.start || 0;
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.start || 0;
};

export const getEndForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.end;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const allSegments = getAllSegments(clip);
    const segment = allSegments.find(segment => segment.id === segmentId);
    if (!segment) {
      throw new Error('Segment not found');
    }
    if (segment.type === SegmentType.VIDEO) {
      return (segment.toFrame - segment.fromFrame) / getClipFPS(clip);
    }
    if (segment.type === SegmentType.COMPOSITE) {
      return segment.currentBounds?.toFrame / getClipFPS(clip);
    }
    return 0;
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.end || 0;
};

/**
 * Filters and repositions element tracks (overlays) for a newly created sub-clip.
 *
 * When creating a clip from a selection, the element tracks in the computed player_config
 * get corrupted by double processing in updateClipDataWithSplits. This function takes the
 * original element tracks and filters/adjusts them to the new clip's time range.
 *
 * @param playerConfig - The player_config from the computed clip (correct segment track)
 * @param originalPlayerConfig - The player_config from the original clip (correct element frames)
 * @param playerStartFrame - The start frame of the selection in the original clip's player timeline
 * @param playerEndFrame - The end frame of the selection in the original clip's player timeline
 */
export const filterAndAdjustElementTracksForSubClip = (
  playerConfig: CompositionConfig | null,
  originalPlayerConfig: CompositionConfig | null,
  playerStartFrame: number,
  playerEndFrame: number
): CompositionConfig | null => {
  if (!playerConfig || !originalPlayerConfig) {
    return playerConfig;
  }
  const updatedTracks = playerConfig.playerConfig.tracks.map(track => {
    if (track.type !== TrackType.ELEMENTS) {
      return track;
    }
    const originalElementsTrack = originalPlayerConfig.playerConfig.tracks.find(
      t => t.type === TrackType.ELEMENTS && (t as ElementsTrack).id === (track as ElementsTrack).id
    ) as ElementsTrack | undefined;
    const elements = originalElementsTrack?.elements ?? (track as ElementsTrack).elements ?? [];
    return {
      ...(track as ElementsTrack),
      elements: elements
        .filter(el => el.toFrame > playerStartFrame && el.fromFrame < playerEndFrame)
        .map(el => ({
          ...el,
          fromFrame: Math.max(0, el.fromFrame - playerStartFrame),
          toFrame: Math.min(playerEndFrame - playerStartFrame, el.toFrame - playerStartFrame)
        }))
    };
  });
  return {
    ...playerConfig,
    playerConfig: {
      ...playerConfig.playerConfig,
      tracks: updatedTracks
    }
  };
};

export const getDurationForSegment = (clip: Clip, segmentId?: string) => {
  const splitsEnabled = isSplitsInTimelineEnabled(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.duration;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.duration || 0;
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.duration || 0;
};
